import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useTags } from 'features/tags';
import {
  useAllTasksFilterDocument,
  useUpdateAllTasksFilterDocument,
} from 'features/user';
import { useCustomOrderByGrouping } from 'pages/all-tasks-filtered/hooks/use-custom-order-by-grouping';
import { useOnReorder } from 'pages/all-tasks-filtered/hooks/use-on-reorder';
import { useSelectedCalendarView } from 'pages/upcoming/hooks/use-selected-calendar-view';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle } from 'shared/assets/icons';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { FilterDocuments } from 'shared/constants';
import { CalendarViewOptions } from 'shared/types/upcoming-view-options';
import { TaskFilterList } from 'shared/views/task-filter-list';

import * as Styled from './side-panel.style';

export type SidePanelProps = {
  onClose: () => void;
};

export const SidePanel: React.FC<SidePanelProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedView = useSelectedCalendarView();

  const goals = useSortedGoals();
  const tags = useTags();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const filterDocument = useAllTasksFilterDocument();
  const customOrder = useCustomOrderByGrouping(filterDocument.groupByMode);
  const updateCustomOrder = useOnReorder(filterDocument.groupByMode);

  const updateFilterDocument = useUpdateAllTasksFilterDocument();

  const onReset = () =>
    updateFilterDocument(FilterDocuments.AllTasks.defaultFilterDocument);

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.Title>{t('pages.upcoming.all-tasks.title')}</Styled.Title>

        <Styled.HeaderInteractions>
          <FilterButton
            filterDocument={filterDocument}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            updateFilterDocument={updateFilterDocument}
            onReset={onReset}
          />
          <IconButton
            icon={XCircle}
            onClick={onClose}
            color={Colors.Default}
            preventDrag
          />
        </Styled.HeaderInteractions>
      </Styled.Header>

      <Styled.Content ref={scrollRef}>
        <TaskFilterList
          filterDocument={filterDocument}
          customOrder={customOrder}
          onReorder={updateCustomOrder}
          scrollElement={scrollRef}
          includeDuration={selectedView !== CalendarViewOptions.Month}
        />
      </Styled.Content>
    </Styled.Container>
  );
};
